import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SITE } from "../lib/site";

const NAV = [
  { label: "About", to: "/#about" },
  { label: "Services", to: "/#services" },
  { label: "Horses", to: "/horses" },
  { label: "Gallery", to: "/gallery" },
  { label: "Contact", to: "/#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // On the home route, the navbar overlays a dark hero — use light text initially.
  // On other routes (e.g. /gallery) we have a light background from the start.
  const overHero = pathname === "/";
  const onLight = scrolled || !overHero;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        onLight
          ? "bg-[#F9F9F6]/85 backdrop-blur-md border-b hairline"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-3 group"
          aria-label="SPJ Sporthorses — home"
        >
          <img
            src={onLight ? "/logo.png" : "/logo-light.png"}
            alt=""
            className="h-12 md:h-14 w-auto select-none transition-opacity"
            draggable={false}
          />
          <span
            className={`hidden sm:inline text-[11px] uppercase tracking-[0.32em] transition-colors ${
              onLight ? "text-[#5C6A61]" : "text-[#F9F9F6]/80"
            }`}
          >
            Sporthorses
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((item) => (
            <a
              key={item.label}
              href={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`text-sm tracking-wide transition-colors duration-300 hover:text-[#965C3B] ${
                onLight ? "text-[#1A2B22]/80" : "text-[#F9F9F6]/90"
              }`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <a
          href="/#contact"
          data-testid="nav-cta-inquire"
          className={`hidden md:inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.22em] transition-colors duration-300 ${
            onLight
              ? "bg-[#1A2B22] text-[#F9F9F6] hover:bg-[#965C3B]"
              : "border border-[#F9F9F6] text-[#F9F9F6] hover:bg-[#F9F9F6] hover:text-[#1A2B22]"
          }`}
        >
          Inquire
          <span aria-hidden>→</span>
        </a>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden p-2 ${onLight ? "text-[#1A2B22]" : "text-[#F9F9F6]"}`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden bg-[#F9F9F6] border-t hairline px-6 py-8 flex flex-col gap-6"
        >
          {NAV.map((item) => (
            <a
              key={item.label}
              href={item.to}
              data-testid={`nav-mobile-${item.label.toLowerCase()}`}
              className="font-display text-3xl tracking-tighter text-[#1A2B22]"
            >
              {item.label}
            </a>
          ))}
          <a
            href="/#contact"
            data-testid="nav-mobile-cta-inquire"
            className="mt-4 inline-flex items-center justify-center px-5 py-4 bg-[#1A2B22] text-[#F9F9F6] text-xs uppercase tracking-[0.22em]"
          >
            Inquire
          </a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
